import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Shield, ArrowRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";

const CONTACT_EMAIL = "info1000arts@gmail.com";
const LAST_UPDATE = "12 octobre 2026";

const SOUS_TRAITANTS = [
  ["Google (Google Sheets)", "Registre des inscriptions et planning des cours", "UE / États-Unis", "Swiss-U.S. Data Privacy Framework"],
  ["Netlify", "Hébergement du site", "États-Unis", "Clauses contractuelles types"],
  ["Render", "Hébergement du serveur (API)", "Allemagne (Francfort)", "Clauses contractuelles types"],
  ["MongoDB Atlas", "Hébergement de la base de données", "UE (AWS, Francfort)", "Clauses contractuelles types"],
  ["Resend", "Envoi des e-mails de confirmation", "États-Unis", "Clauses contractuelles types"],
  ["Anthropic (Claude)", "Assistant conversationnel du site (questions posées par les visiteurs)", "États-Unis", "Clauses contractuelles types"],
  ["Meta (Instagram)", "Messages privés reçus sur le compte Instagram de l'école et réponses de l'assistant", "UE (Irlande) / États-Unis", "Swiss-U.S. Data Privacy Framework"],
  ["Calendly", "Réservation des créneaux de cours privés", "États-Unis", "Clauses contractuelles types"],
  ["YouTube (Google)", "Lecture des vidéos intégrées", "États-Unis", "Swiss-U.S. Data Privacy Framework"],
  ["Zoom", "Cours en ligne, uniquement en cas de situation sanitaire exceptionnelle", "États-Unis", "Clauses contractuelles types"],
];

export default function MentionsLegales() {
  useSEO({
    title: "Mentions légales & protection des données · 1000 ARTS Cugy VD",
    description:
      "Mentions légales de 1000 ARTS, école de danse à Cugy VD, et protection des données : ce que nous collectons via les formulaires d'inscription, pourquoi, avec qui c'est partagé et comment exercer vos droits.",
    canonical: "/mentions-legales",
  });

  return (
    <div data-testid="page-mentions-legales" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[860px] mx-auto px-6 md:px-12 relative z-10">
        <div className="eyebrow mb-3">Informations légales</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-tight">
          Mentions légales &amp;<br />
          <span style={{ color: "var(--primary)" }}>protection des données</span>
        </h1>
        <p className="mt-6 text-lg text-[var(--muted)] leading-relaxed">
          Nous inscrivons des enfants. Cette page dit ce que nous collectons, pourquoi, et comment
          vous gardez la main dessus.
        </p>
        <p className="mt-3 text-sm text-[var(--muted)]">Dernière mise à jour : {LAST_UPDATE}</p>

        {/* ------------------------------------------------------------------ EN BREF */}
        <div
          className="card-flat mt-10"
          data-testid="legal-en-bref"
          style={{ background: "#FFF7ED", borderLeft: "4px solid #F97316", borderColor: "transparent" }}
        >
          <div className="flex items-start gap-3">
            <Shield size={20} className="shrink-0 mt-0.5" style={{ color: "#EA580C" }} />
            <div>
              <div className="font-display text-lg mb-2" style={{ color: "#7C2D12" }}>En bref</div>
              <p className="text-sm leading-relaxed" style={{ color: "#7C2D12" }}>
                Nous ne collectons que ce qu'il faut pour inscrire un élève, organiser les cours et
                facturer. Nous ne vendons aucune donnée et n'envoyons aucune publicité. Les photos ne
                sont publiées que si vous l'avez accepté à l'inscription, et refuser ne change rien à
                la place de votre enfant. Pour voir, corriger ou supprimer vos données, un e-mail à{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold" style={{ color: "#7C2D12" }}>
                  {CONTACT_EMAIL}
                </a>{" "}
                suffit — réponse sous 30 jours.
              </p>
            </div>
          </div>
        </div>

        {/* ----------------------------------------------------------------- ÉDITEUR */}
        <Section id="editeur" title="1. Éditeur du site" testId="section-editeur">
          <div className="card-flat" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
            <div className="font-display text-xl mb-1">1000 ARTS</div>
            <div className="text-sm mb-3">Entreprise individuelle Miller Xavier Sacon Baquerizo</div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
                Chemin des Dailles 10E, 1053 Cugy VD, Suisse
              </li>
              <li className="flex items-start gap-3" data-testid="legal-ide">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--primary)" }} />
                IDE : CHE-344.837.401 — non inscrite au registre du commerce
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
                <a href="tel:+41799504031" className="hover:underline" data-testid="legal-tel">079 950 40 31</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:underline break-all" data-testid="legal-email">{CONTACT_EMAIL}</a>
              </li>
            </ul>
          </div>
          <P>
            1000 ARTS est une école de danse, de chant et de comédie musicale située à Cugy, dans le
            canton de Vaud.
          </P>
          <P>
            Responsable du traitement des données : Miller Xavier Sacon Baquerizo. Pour toute question
            relative à ce site ou à vos données, écrivez-nous à{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: "var(--primary)" }}>{CONTACT_EMAIL}</a>.
          </P>
          <P>
            Hébergement : Netlify (site), Render à Francfort (serveur) et MongoDB Atlas à
            Francfort (base de données). Site réalisé par l'agence Lemon (heylemon.ch).
          </P>
        </Section>

        {/* ------------------------------------------------------------------ DONNÉES */}
        <Section id="donnees" title="2. Ce que nous collectons et pourquoi" testId="section-donnees">
          <P>
            Nous ne collectons que ce qui est nécessaire pour inscrire un élève, organiser les cours
            et facturer. Nous ne vendons aucune donnée, à personne, et nous n'envoyons aucune
            publicité.
          </P>
          <P>
            Selon le formulaire utilisé — cours d'essai, inscription semestrielle, stage ou audition —
            nous collectons le prénom, le nom et la date de naissance de l'élève, le prénom, le nom,
            l'adresse, l'e-mail et le téléphone du représentant légal ou de l'adulte inscrit, le cours
            ou le stage choisi, et le contenu du champ commentaire. Ces informations nous servent à
            traiter l'inscription et à gérer la liste d'attente, à vous écrire au sujet des cours —
            horaires, annulations, spectacles —, à établir les factures et à respecter nos obligations
            comptables, et à assurer la sécurité de l'élève pendant les cours.
          </P>
          <P>
            <strong>Informations de santé.</strong> Si vous nous transmettez une allergie, un asthme,
            une blessure ou un besoin particulier — dans le champ commentaire, par un certificat
            médical en cas d'absences répétées, ou par l'annonce d'une blessure demandée aux groupes
            compétition — cette information n'est communiquée qu'au professeur concerné et à la
            direction, uniquement pour la sécurité de l'élève. Vous choisissez librement de nous la
            communiquer.
          </P>
          <P>
            <strong>Élèves mineurs.</strong> Les formulaires d'inscription sont réservés aux
            représentants légaux : un enfant ne peut pas s'inscrire seul. En remplissant un
            formulaire, vous confirmez être le parent ou le représentant légal de l'élève et consentir
            en son nom au traitement décrit sur cette page.
          </P>
          <P>
            Les conditions d'inscription, de paiement, d'absence et d'annulation ne figurent pas ici :
            elles sont dans le règlement de l'école, accessible depuis chaque formulaire.
          </P>
          <P>
            Nous conservons ces données pendant la durée de l'inscription, puis le temps nécessaire au
            suivi de l'élève. Les pièces comptables sont conservées dix ans, comme l'exige le droit
            suisse.
          </P>
          <P>
            Les questions posées à l'assistant du site sont conservées un an, sans votre adresse
            IP, pour améliorer ses réponses. Pour nos statistiques, nous notons seulement le lieu
            approximatif d'où la conversation est écrite (pays, canton ou ville, déduits de la
            connexion par notre hébergeur, ou le fuseau horaire de l'appareil). L'assistant n'a
            accès à aucun dossier d'élève : n'y indiquez pas de données personnelles.
          </P>
          <P>
            Les messages privés envoyés au compte Instagram de l'école sont lus par le même
            assistant, qui répond aux questions sur l'école en se présentant comme assistant
            virtuel. Ils sont gardés un an dans notre console, avec l'identifiant du compte
            Instagram qui écrit, puis effacés. Pour en demander l'effacement plus tôt, voir « Vos
            droits » ci-dessous.
          </P>
        </Section>

        {/* ----------------------------------------------------------------- PARTAGE */}
        <Section id="destinataires" title="3. Avec qui elles sont partagées" testId="section-destinataires">
          <P>
            Vos données restent au sein de l'école : la direction, l'administration et, pour ce qui le
            concerne, le professeur du cours. Nous faisons appel à des prestataires techniques qui les
            traitent pour notre compte et sur nos instructions :
          </P>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="subprocessors-table">
              <thead>
                <tr className="text-left border-b-2" style={{ borderColor: "var(--primary)" }}>
                  <th className="py-2 pr-4 font-semibold">Prestataire</th>
                  <th className="py-2 pr-4 font-semibold">Rôle</th>
                  <th className="py-2 pr-4 font-semibold">Pays d'hébergement</th>
                  <th className="py-2 font-semibold">Base du transfert</th>
                </tr>
              </thead>
              <tbody className="align-top">
                {SOUS_TRAITANTS.map(([who, role, pays, base]) => (
                  <tr key={who} className="border-b border-[var(--border-soft)]">
                    <td className="py-3 pr-4 font-medium">{who}</td>
                    <td className="py-3 pr-4 text-[var(--muted)]">{role}</td>
                    <td className="py-3 pr-4 text-[var(--muted)]">{pays}</td>
                    <td className="py-3 text-[var(--muted)]">{base}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <P>
            Le Conseil fédéral reconnaît depuis le 15 septembre 2024 un niveau de protection adéquat
            pour les entreprises américaines certifiées sous le Swiss-U.S. Data Privacy Framework.
            Pour les autres, le transfert repose sur les clauses contractuelles types reconnues par le
            Préposé fédéral. Si ce point est important pour vous, écrivez-nous : nous pouvons traiter
            votre inscription par téléphone ou sur place, sans passer par les formulaires du site.
          </P>
        </Section>

        {/* ------------------------------------------------------------------ PHOTOS */}
        <Section id="photos" title="4. Photos et vidéos" testId="section-photos">
          <P>
            Des photos et des vidéos sont prises pendant les cours, les répétitions et les spectacles.
            Elles ne sont publiées sur le site et sur les réseaux sociaux de l'école que si vous
            l'avez accepté lors de l'inscription. Ce choix est libre : le refuser ne change rien à la
            place de l'élève, à son groupe ni à son tarif. Vous pouvez le retirer à tout moment par
            simple e-mail, sans justification.
          </P>
          <P>
            Si vous apparaissez sur un contenu et souhaitez son retrait, écrivez-nous : nous le
            retirerons sans discussion et dans les meilleurs délais.
          </P>
          <P>
            Les chorégraphies, vidéos et supports pédagogiques créés par l'école restent sa propriété
            et ne peuvent être reproduits sans son autorisation écrite.
          </P>
        </Section>

        {/* ------------------------------------------------------------------- DROITS */}
        <Section id="droits" title="5. Vos droits" testId="section-droits">
          <P>
            Conformément à la loi fédérale sur la protection des données (LPD), vous pouvez à tout
            moment savoir quelles données nous détenons sur vous ou sur votre enfant et en recevoir
            une copie, faire corriger une information inexacte, demander la suppression de ce que nous
            ne sommes pas tenus de conserver, retirer votre accord au droit à l'image, et vous opposer
            à un traitement.
          </P>
          <div className="card-flat mt-6" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
            <p className="text-sm leading-relaxed">
              Un e-mail à{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold" style={{ color: "var(--primary)" }}>{CONTACT_EMAIL}</a>{" "}
              suffit, en indiquant le nom de l'élève concerné (pour un message Instagram : le nom du
              compte Instagram). Nous répondons dans un délai de 30
              jours au maximum, gratuitement, et aucune formule juridique n'est attendue. Vous pouvez
              également vous adresser au Préposé fédéral à la protection des données et à la
              transparence (PFPDT), à Berne.
            </p>
          </div>
        </Section>

        {/* ------------------------------------------------------------------ COOKIES */}
        <Section id="cookies" title="6. Cookies et statistiques de visite" testId="section-cookies">
          <P>
            Ce site ne dépose aucun cookie publicitaire et n'utilise aucun outil de profilage. Seuls
            des cookies techniques nécessaires à son fonctionnement peuvent être utilisés. Les vidéos
            et l'assistant conversationnel ne se chargent qu'au moment où vous les activez ; ils
            peuvent alors déposer leurs propres cookies, que vous pouvez refuser depuis les réglages
            de votre navigateur.
          </P>
          <P>
            Pour savoir quelles pages sont utiles, nous comptons les visites nous-mêmes, sans cookie
            et sans service extérieur : les pages vues, le temps passé sur chacune, la page d'où
            vous arrivez (un moteur de recherche, Instagram…), le type d'appareil et le lieu
            approximatif (pays, canton ou ville, déduits de la connexion par notre hébergeur).
            Votre adresse IP n'est jamais enregistrée : pour reconnaître une même visite dans la
            journée, nous n'en gardons qu'une empreinte brouillée par un code qui change chaque
            jour et qui est effacé le lendemain. Votre navigateur garde seulement l'indication
            que vous êtes déjà venu. Ces chiffres ne servent qu'à l'école et sont effacés après
            25 mois.
          </P>
        </Section>

        {/* -------------------------------------------------------------------- DROIT */}
        <Section id="droit-applicable" title="7. Droit applicable" testId="section-droit-applicable">
          <P>
            Les présentes mentions sont soumises au droit suisse. Les fors légaux s'appliquent.
          </P>
        </Section>

        {/* CTA */}
        <div
          className="mt-16 card-flat text-center"
          style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent", padding: "40px 28px" }}
        >
          <h3 className="font-display text-2xl md:text-3xl">Une question sur vos données&nbsp;?</h3>
          <p className="text-white/90 mt-3 text-sm md:text-base">
            Écrivez-nous, on vous répond en français, sans jargon.
          </p>
          <Link
            to="/contact"
            data-testid="legal-contact-cta"
            className="inline-flex items-center gap-2 mt-6 px-8 py-4 rounded-full bg-white font-semibold"
            style={{ color: "var(--primary)" }}
          >
            Nous contacter <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ id, title, children, testId }) {
  return (
    <section id={id} data-testid={testId} className="mt-14 scroll-mt-28">
      <h2 className="font-display text-2xl md:text-3xl tracking-tight pb-3 mb-5 border-b-2" style={{ borderColor: "var(--primary)" }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function P({ children }) {
  return <p className="text-[15px] leading-relaxed text-[var(--text)] mt-4">{children}</p>;
}
